/** How far from the left edge a finger may start and still count as back. */
export const SWIPE_BACK_EDGE_PX = 96;

/**
 * Browsers that already own a back gesture (Safari, Chrome) only listen on the
 * outer bezel. Leave that strip to them so one swipe does not go back twice.
 * Home-screen / standalone mode has no browser gesture, so the strip is ours.
 */
export const SWIPE_BACK_NATIVE_EDGE_PX = 24;

/** Horizontal distance that completes the gesture. */
export const SWIPE_BACK_MIN_DISTANCE_PX = 72;

const BLOCK_SELECTOR = [
  'input',
  'textarea',
  'select',
  'canvas',
  '[contenteditable="true"]',
  '[role="slider"]',
  '[data-swipe-back-ignore]',
  '[data-swipe-back-block]',
].join(',');

type SwipeEdgeWindow = {
  navigator: Navigator | { standalone?: boolean };
  matchMedia: (query: string) => { matches: boolean };
};

function launchedFromHomeScreen(navigator: SwipeEdgeWindow['navigator']): boolean {
  return 'standalone' in navigator && navigator.standalone === true;
}

export function historyCanGoBack(state: unknown): boolean {
  if (!state || typeof state !== 'object') return false;
  const idx = (state as { idx?: unknown }).idx;
  return typeof idx === 'number' && Number.isFinite(idx) && idx > 0;
}

export function browserOwnsOuterSwipeEdge(win: SwipeEdgeWindow = window as unknown as SwipeEdgeWindow): boolean {
  if (launchedFromHomeScreen(win.navigator)) return false;
  return !win.matchMedia('(display-mode: standalone), (display-mode: fullscreen), (display-mode: minimal-ui)').matches;
}

export function swipeBackStartAllowed(clientX: number, browserOwnsOuterEdge: boolean): boolean {
  if (clientX > SWIPE_BACK_EDGE_PX) return false;
  if (browserOwnsOuterEdge && clientX <= SWIPE_BACK_NATIVE_EDGE_PX) return false;
  return clientX >= 0;
}

export type SwipeBackMove = 'pending' | 'back' | 'cancel';

/** Decide while the finger is down. `back` means we should claim the touch. */
export function classifySwipeBackMove(dx: number, dy: number): SwipeBackMove {
  const ady = Math.abs(dy);
  if (dx < -12 || (ady > 28 && ady > dx)) return 'cancel';
  if (dx >= 10 && dx > ady) return 'back';
  return 'pending';
}

export function swipeBackShouldNavigate(dx: number, dy: number): boolean {
  return dx >= SWIPE_BACK_MIN_DISTANCE_PX && dx > Math.abs(dy) * 1.2;
}

function eventElement(target: EventTarget | null): Element | null {
  if (target instanceof Element) return target;
  if (target instanceof Node) return target.parentElement;
  return null;
}

export function elementBlocksSwipeBack(target: EventTarget | null): boolean {
  const el = eventElement(target);
  if (!el) return true;
  return el.closest(BLOCK_SELECTOR) != null;
}

/** A row that is already scrolled sideways should keep a right-swipe. */
export function ancestorScrollsHorizontally(target: EventTarget | null): boolean {
  let el = eventElement(target);
  while (el) {
    if (el instanceof HTMLElement) {
      const overflowX = getComputedStyle(el).overflowX;
      if (
        (overflowX === 'auto' || overflowX === 'scroll') &&
        el.scrollWidth > el.clientWidth + 1 &&
        el.scrollLeft > 1
      ) {
        return true;
      }
    }
    el = el.parentElement;
  }
  return false;
}
